import React, { useCallback, useEffect, useState } from 'react';
import Coachmark from './tutorial/Coachmark';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useQuests } from '../context/QuestContext';
import { useCharacterOptional } from '../context/CharacterContext';
import AuthLayeredCard from './AuthLayeredCard';
import StoryQuests from './StoryQuests';
import AchievementsPanel from './AchievementsPanel';
import { authButtonSmallStyle } from '../constants/authStyles';
import { pendingRuhVideos, useRewardedAds } from '../utils/rewardedAds';
import { tl } from '../utils/inlineI18n';
import { useQuestNavigator } from '../utils/questNavigator';
import './Auth.css';
import './Battle.css';
import './Village.css';
import './ItemMarket.css';
import './LevelQuests.css';

const LevelQuests = ({ username }) => {
  const { t } = useTranslation('gameData');
  const { t: tc } = useTranslation('common');
  const {
    isOpen,
    closeQuests,
    quests,
    dailyQuest,
    loading,
    refreshQuests,
    setQuests,
    story,
    storyCount,
    setStory,
    openTab,
  } = useQuests();
  const [panelTab, setPanelTab] = useState('story');
  const character = useCharacterOptional();
  const refreshCharacter = character?.refreshCharacter;
  const { goHunt } = useQuestNavigator();
  const [claimLoading, setClaimLoading] = useState(null);
  const ads = useRewardedAds();
  const [adMsg, setAdMsg] = useState('');
  const adRuhCount = ads.status?.counts?.ruh ?? 0;
  const adRuhLimit = ads.status?.limits?.ruh ?? 10;
  const pendingVideos = pendingRuhVideos(ads);

  const handleWatchAd = useCallback(async () => {
    setAdMsg('');
    const res = await ads.watch('ruh');
    if (res?.success) {
      setAdMsg(res.message || '');
      await refreshCharacter?.().catch(() => {});
    } else if (res?.message) {
      setAdMsg(res.message);
    }
  }, [ads, refreshCharacter]);

  useEffect(() => {
    if (isOpen && username) {
      refreshQuests(username);
    }
  }, [isOpen, username, refreshQuests]);

  // Ücretsiz Ruh balonundan açılınca doğrudan video kartının olduğu sekme seçilir.
  useEffect(() => {
    if (isOpen && openTab) setPanelTab(openTab);
  }, [isOpen, openTab]);

  const handleClaim = useCallback(async (questLevel) => {
    if (!username || claimLoading != null) return;
    setClaimLoading(questLevel);
    try {
      const res = await axios.post('/api/level-quests/claim', {
        username,
        questLevel
      });
      if (res.data?.success) {
        if (Array.isArray(res.data.quests)) {
          setQuests(res.data.quests);
        }
        await refreshQuests(username);
        await refreshCharacter?.().catch(() => {});
      }
    } finally {
      setClaimLoading(null);
    }
  }, [username, claimLoading, setQuests, refreshQuests, refreshCharacter]);

  const handleDailyClaim = useCallback(async () => {
    if (!username || claimLoading != null) return;
    setClaimLoading('daily');
    try {
      const res = await axios.post('/api/daily-quests/claim', { username });
      if (res.data?.success) {
        await refreshCharacter?.().catch(() => {});
        await refreshQuests(username);
      }
    } finally {
      setClaimLoading(null);
    }
  }, [username, claimLoading, refreshCharacter, refreshQuests]);

  if (!isOpen) return null;

  // Ödülü alınmış (biten) görevler listede görünmez.
  const openQuests = quests.filter((q) => !q.claimed);
  const hasDaily = dailyQuest?.available && !dailyQuest.claimed;
  const showEmpty = !loading && openQuests.length === 0 && !hasDaily && !ads.supported;
  const levelCount = openQuests.filter((q) => q.ready).length + (hasDaily && dailyQuest.ready ? 1 : 0);

  return createPortal(
    <div
      className="popup-overlay item-market-overlay level-quests-overlay"
      onClick={closeQuests}
      role="presentation"
    >
      <Coachmark
        tourId="quests"
        steps={[
          { selector: '.level-quests-list', textKey: 'questsDaily' },
      ]}
      />
      <AuthLayeredCard
        className="item-market-card level-quests-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="item-market-header">
          <div className="item-market-header__info">
            <h2 className="item-market-title">{t('questPanelTitle')}</h2>
          </div>
          <button
            type="button"
            className="item-market-close"
            onClick={closeQuests}
            aria-label={tc('close')}
          >
            ✕
          </button>
        </div>

        <div className="battle-nav-row item-market-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={panelTab === 'story'}
            className={`auth-button auth-button--compact battle-nav-btn${panelTab === 'story' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => setPanelTab('story')}
          >
            {t('questTabStory')}
            {storyCount > 0 ? <span className="level-quests-tab__badge">{storyCount}</span> : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={panelTab === 'level'}
            className={`auth-button auth-button--compact battle-nav-btn${panelTab === 'level' ? ' battle-subtab--active' : ''}${pendingVideos > 0 && panelTab !== 'level' ? ' level-quests-tab--attention' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => setPanelTab('level')}
          >
            {t('questTabLevel')}
            {levelCount > 0 ? <span className="level-quests-tab__badge">{levelCount}</span> : null}
            {pendingVideos > 0 ? (
              <span className="level-quests-tab__badge level-quests-tab__badge--video" title={tl('İzlenmemiş ödüllü video', 'Unwatched reward videos')}>
                🎬{pendingVideos}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={panelTab === 'achievements'}
            className={`auth-button auth-button--compact battle-nav-btn${panelTab === 'achievements' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => setPanelTab('achievements')}
          >
            {tl('Başarımlar', 'Achievements')}
          </button>
        </div>

        {panelTab === 'achievements' ? (
          <div className="item-market-body">
            <AchievementsPanel onCharacterChange={() => refreshCharacter?.().catch(() => {})} />
          </div>
        ) : null}

        {panelTab === 'achievements' ? null : panelTab === 'story' ? (
          <div className="item-market-body">
            <StoryQuests
              username={username}
              view={story}
              onStoryChange={setStory}
              onCharacterChange={() => refreshCharacter?.().catch(() => {})}
            />
          </div>
        ) : (
        <div className="item-market-body">
          {loading && openQuests.length === 0 && !hasDaily ? (
            <div className="item-market-loading">{tc('loading')}</div>
          ) : null}
          {showEmpty ? <p className="item-market-empty">{t('levelQuestEmpty')}</p> : null}

          {hasDaily ? (
            <>
              <h3 className="level-quests-section-title">{t('dailyQuestTitle')}</h3>
              <ul className="level-quests-list">
                <li
                  className={`item-market-product-card level-quests-item${
                    dailyQuest.claimed ? ' level-quests-item--claimed' : ''
                  }`}
                >
                  <div className="level-quests-item__head">
                    <span className="item-market-product-card__name level-quests-item__title">
                      {t('dailyQuestTitle')}
                    </span>
                    <span className="level-quests-item__progress">
                      {t('levelQuestProgress', {
                        current: dailyQuest.progress,
                        total: dailyQuest.killCount
                      })}
                    </span>
                  </div>
                  <p className="item-market-product-card__detail level-quests-item__muted">
                    {t('dailyQuestOncePerDay')}
                  </p>
                  <p
                    className={`item-market-product-card__detail${!dailyQuest.ready ? ' level-quests-go' : ''}`}
                    onClick={!dailyQuest.ready ? () => goHunt({ monsterId: dailyQuest.monsterId, monster: dailyQuest.monsterName }) : undefined}
                    role={!dailyQuest.ready ? 'button' : undefined}
                  >
                    {t('levelQuestObjective', {
                      count: dailyQuest.killCount,
                      name: dailyQuest.monsterName || `#${dailyQuest.monsterId}`
                    })}
                    {!dailyQuest.ready ? (
                      <button
                        type="button"
                        className="quest-go-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          goHunt({ monsterId: dailyQuest.monsterId, monster: dailyQuest.monsterName });
                        }}
                      >
                        {tl('Göreve git', 'Go')}
                      </button>
                    ) : null}
                  </p>
                  <p className="item-market-product-card__price">
                    {t('dailyQuestRewards', { soul: dailyQuest.soulReward })}
                  </p>
                  {dailyQuest.claimed ? (
                    <span className="level-quests-item__done">{t('levelQuestClaimed')}</span>
                  ) : dailyQuest.ready ? (
                    <button
                      type="button"
                      className="auth-button auth-button--compact item-market-buy-btn level-quests-claim-btn"
                      disabled={claimLoading === 'daily'}
                      onClick={handleDailyClaim}
                    >
                      {claimLoading === 'daily' ? '…' : t('levelQuestClaim')}
                    </button>
                  ) : null}
                </li>
              </ul>
            </>
          ) : null}

          {ads.supported ? (
            <ul className="level-quests-list">
              <li className={`item-market-product-card level-quests-item${adRuhCount >= adRuhLimit ? ' level-quests-item--claimed' : ' level-quests-item--video-pending'}`}>
                <div className="level-quests-item__head">
                  <span className="item-market-product-card__name level-quests-item__title">
                    {tl('🎬 Günlük Video', '🎬 Daily Video')}
                  </span>
                  <span className="level-quests-item__progress">{adRuhCount}/{adRuhLimit}</span>
                </div>
                <p className="item-market-product-card__detail">
                  {tl(`Günde ${adRuhLimit} video izle, her video için ödül al.`, `Watch up to ${adRuhLimit} videos a day, get a reward for each.`)}
                </p>
                <p className="item-market-product-card__price">{tl('+1 Ruh Parası', '+1 Soul Coin')}</p>
                {adMsg ? <p className="item-market-product-card__detail level-quests-item__muted">{adMsg}</p> : null}
                {adRuhCount >= adRuhLimit ? (
                  <span className="level-quests-item__done">{tl('Bugünlük bitti', 'Done for today')}</span>
                ) : (
                  <button
                    type="button"
                    className="auth-button auth-button--compact item-market-buy-btn level-quests-claim-btn"
                    disabled={!!ads.watching}
                    onClick={handleWatchAd}
                  >
                    {ads.watching === 'ruh' ? '…' : tl('Video izle', 'Watch video')}
                  </button>
                )}
              </li>
            </ul>
          ) : null}

          {openQuests.length > 0 ? (
            <>
              <h3 className="level-quests-section-title">{t('levelQuestTitle')}</h3>
              <ul className="level-quests-list">
                {openQuests.map((q) => (
                  <li
                    key={q.level}
                    className={`item-market-product-card level-quests-item${
                      q.claimed ? ' level-quests-item--claimed' : ''
                    }`}
                  >
                    <div className="level-quests-item__head">
                      <span className="item-market-product-card__name level-quests-item__title">
                        {q.title?.trim() ? q.title : t('levelQuestItemTitle', { level: q.level })}
                      </span>
                      <span className="level-quests-item__progress">
                        {t('levelQuestProgress', { current: q.progress, total: q.killCount })}
                      </span>
                    </div>
                    {q.story ? <p className="item-market-product-card__desc">{q.story}</p> : null}
                    <p
                      className={`item-market-product-card__detail${!q.ready ? ' level-quests-go' : ''}`}
                      onClick={!q.ready ? () => goHunt({ monsterId: q.monsterId, monster: q.monsterName }) : undefined}
                      role={!q.ready ? 'button' : undefined}
                    >
                      {t('levelQuestObjective', {
                        count: q.killCount,
                        name: q.monsterName || `#${q.monsterId}`
                      })}
                      {!q.ready ? (
                        <button
                          type="button"
                          className="quest-go-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            goHunt({ monsterId: q.monsterId, monster: q.monsterName });
                          }}
                        >
                          {tl('Göreve git', 'Go')}
                        </button>
                      ) : null}
                    </p>
                    <p className="item-market-product-card__price">
                      {t('levelQuestRewards', { exp: q.expReward, yang: q.yangReward })}
                    </p>
                    {q.claimed ? (
                      <span className="level-quests-item__done">{t('levelQuestClaimed')}</span>
                    ) : q.ready ? (
                      <button
                        type="button"
                        className="auth-button auth-button--compact item-market-buy-btn level-quests-claim-btn"
                        disabled={claimLoading === q.level}
                        onClick={() => handleClaim(q.level)}
                      >
                        {claimLoading === q.level ? '…' : t('levelQuestClaim')}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
        )}
      </AuthLayeredCard>
    </div>,
    document.body
  );
};

export default LevelQuests;
